import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Send, Eye, Save, Paperclip, Loader2 } from "lucide-react";

/**
 * MemberEmailComposer
 * Dialog réutilisable pour envoyer un devis/acompte/facture/adhesion à un membre, avec :
 *  - Sélecteur d'entreprise membre (ou préchargé via `defaultEntrepriseId`)
 *  - Templates prédéfinis + apprentissage automatique
 *  - OBJET + CORPS éditables (HTML simple)
 *  - Amélioration IA (Claude) avec prévisualisation
 *  - Pièce jointe PDF (devis/acompte/facture) via `attachAppel`
 *  - Archivage automatique dans la GED
 */
export default function MemberEmailComposer({
  open,
  onOpenChange,
  defaultEntrepriseId,
  attachAppel = null,     // { id, type: 'devis'|'acompte'|'facture'|'adhesion', reference }
  onSent,
}) {
  const [entreprises, setEntreprises] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [entrepriseId, setEntrepriseId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [objet, setObjet] = useState("");
  const [corps, setCorps] = useState("");
  const [preview, setPreview] = useState(null); // {objet, corps_html}
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateCode, setTemplateCode] = useState("");
  const [templateLibelle, setTemplateLibelle] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const selectedEntreprise = useMemo(
    () => entreprises.find(e => e.id === entrepriseId),
    [entreprises, entrepriseId]
  );

  useEffect(() => {
    if (!open) return;
    api.get("/entreprises").then(({ data }) => setEntreprises(data || [])).catch(() => {});
    const cat = attachAppel?.type || undefined;
    api.get("/email-templates", { params: cat ? { categorie: cat } : {} }).then(({ data }) => setTemplates(data || [])).catch(() => {});
    setEntrepriseId(defaultEntrepriseId || "");
    setPreview(null); setInstruction(""); setSaveAsTemplate(false);
  }, [open, defaultEntrepriseId, attachAppel]);

  useEffect(() => {
    if (!selectedEntreprise) return;
    setToEmail(selectedEntreprise.dirigeant_email || selectedEntreprise.contact_email || "");
  }, [selectedEntreprise]);

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setSelectedTemplateId(tplId);
    const ctx = {
      "{{reference}}": attachAppel?.reference || "",
      "{{dirigeant_nom}}": selectedEntreprise?.dirigeant_nom || selectedEntreprise?.representant_legal || "",
      "{{raison_sociale}}": selectedEntreprise?.raison_sociale || "",
    };
    let o = tpl.objet || "", c = tpl.corps || "";
    Object.entries(ctx).forEach(([k, v]) => { o = o.replaceAll(k, v); c = c.replaceAll(k, v); });
    setObjet(o); setCorps(c);
  };

  const aiImprove = async () => {
    if (!objet && !corps) { toast.error("Rédigez d'abord un objet et un corps"); return; }
    setAiLoading(true); setPreview(null);
    try {
      const { data } = await api.post("/emails/ai-improve", {
        objet, corps,
        instruction: instruction || null,
        contexte_entreprise_id: entrepriseId || null,
      });
      setPreview(data);
      toast.success("Prévisualisation IA prête");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur IA");
    } finally { setAiLoading(false); }
  };

  const acceptPreview = () => {
    if (!preview) return;
    setObjet(preview.objet); setCorps(preview.corps_html); setPreview(null);
    toast.success("Modifications IA appliquées");
  };

  const send = async () => {
    if (!entrepriseId) { toast.error("Sélectionnez un membre"); return; }
    if (!objet || !corps) { toast.error("Objet et corps obligatoires"); return; }
    setSending(true);
    try {
      const payload = {
        entreprise_id: entrepriseId,
        to_email: toEmail || null,
        objet,
        corps_html: corps,
        appel_id: attachAppel?.id || null,
        attach_type: attachAppel?.type || null,
        template_id: selectedTemplateId || null,
        save_as_template: !!saveAsTemplate,
        template_code: templateCode || undefined,
        template_libelle: templateLibelle || undefined,
        template_categorie: attachAppel?.type || "autre",
      };
      const { data } = await api.post("/emails/send-member", payload);
      toast.success(`Email envoyé ${data.status === "sent" ? "✓" : "(mock)"} · Archivé GED`);
      onOpenChange?.(false);
      onSent?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Envoi impossible");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Envoyer un email à un membre
            {attachAppel && <Badge className="bg-[#C5A059] text-white uppercase">{attachAppel.type}</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Sélecteur membre */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Entreprise membre *</label>
              <Select value={entrepriseId} onValueChange={setEntrepriseId}>
                <SelectTrigger data-testid="email-entreprise-select">
                  <SelectValue placeholder="Choisir un membre…" />
                </SelectTrigger>
                <SelectContent>
                  {entreprises.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.raison_sociale} {e.territoire && `· ${e.territoire}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Email destinataire</label>
              <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="dirigeant@…" data-testid="email-to" />
            </div>
          </div>

          {/* Pièce jointe */}
          {attachAppel && (
            <div className="flex items-center gap-2 bg-[#FDF9F1] border border-[#C5A059]/30 rounded-md p-2 text-sm">
              <Paperclip className="h-4 w-4 text-[#A88846]" />
              <span>Pièce jointe PDF : <strong>{attachAppel.type.toUpperCase()} {attachAppel.reference}</strong></span>
            </div>
          )}

          {/* Templates */}
          {templates.length > 0 && (
            <div>
              <label className="text-xs font-medium text-slate-600">Modèle d'email prédéfini</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t.id)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${selectedTemplateId === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"}`}
                    data-testid={`template-${t.code}`}
                  >
                    {t.libelle} {t.usage_count > 0 && <span className="opacity-60">({t.usage_count})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Composition */}
          <div>
            <label className="text-xs font-medium text-slate-600">Objet *</label>
            <Input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Objet de l'email" data-testid="email-objet" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Corps (HTML simple accepté) *</label>
            <Textarea value={corps} onChange={(e) => setCorps(e.target.value)} rows={8} placeholder="<p>Bonjour,</p>…" data-testid="email-corps" />
          </div>

          {/* Amélioration IA */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-[#C5A059]" />Améliorer avec Claude 4.5
            </div>
            <div className="flex gap-2">
              <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Instruction (ex: ton plus formel, ajouter paragraphe retard)" />
              <Button onClick={aiImprove} disabled={aiLoading} variant="outline" data-testid="ai-improve-btn">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Prévisualiser</>}
              </Button>
            </div>
            {preview && (
              <div className="bg-white border border-emerald-300 rounded p-3 space-y-2">
                <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><Eye className="h-3.5 w-3.5" />Prévisualisation IA</div>
                <div><strong>Objet :</strong> {preview.objet}</div>
                <div className="prose prose-sm max-w-none border-t pt-2" dangerouslySetInnerHTML={{ __html: preview.corps_html }} />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setPreview(null)}>Rejeter</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={acceptPreview} data-testid="accept-preview-btn">Appliquer</Button>
                </div>
              </div>
            )}
          </div>

          {/* Sauvegarder comme modèle */}
          <div className="border-t pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={saveAsTemplate} onCheckedChange={setSaveAsTemplate} data-testid="save-as-template" />
              <Save className="h-4 w-4" />Enregistrer comme modèle pour prochaine fois
            </label>
            {saveAsTemplate && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input placeholder="Code (ex: relance_impayé)" value={templateCode} onChange={(e) => setTemplateCode(e.target.value)} />
                <Input placeholder="Libellé affiché" value={templateLibelle} onChange={(e) => setTemplateLibelle(e.target.value)} />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="send-email-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Envoyer et archiver GED
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
